/**
 * FormFlu Hero Section
 * 
 * The main landing section featuring the "sick form → healthy form" 
 * transformation story with the brand mascots.
 */

import './Hero.css';

interface HeroProps {
  onGetStarted?: () => void;
  onWatchDemo?: () => void;
}

export function Hero({ onGetStarted, onWatchDemo }: HeroProps) {
  return (
    <section className="hero">
      <div className="hero-container container">
        <div className="hero-content">
          {/* Badge */}
          <div className="hero-badge">
            <span className="badge badge-teal">🏥 HIPAA Compliant</span>
          </div>

          {/* Headline */}
          <h1 className="hero-title">
            Turn <span className="hero-highlight-sick">Sick Forms</span>
            <br />
            Into <span className="hero-highlight-healthy">Healthy Ones</span>
          </h1>

          {/* Subheadline */}
          <p className="hero-subtitle">
            Your forms are underperforming. Low conversions. High abandonment. 
            Patient data compliance headaches. <strong>FormFlu</strong> is the cure — 
            a HIPAA-first form builder with A/B testing that optimizes every step.
          </p>

          {/* CTA Buttons */}
          <div className="hero-actions">
            <button 
              onClick={onGetStarted}
              className="btn btn-primary btn-lg"
            >
              Start Free Trial
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
            <button 
              onClick={onWatchDemo}
              className="btn btn-secondary btn-lg"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
              </svg>
              Watch Demo
            </button>
          </div>

          {/* Trust Indicators */}
          <div className="hero-trust">
            <div className="hero-trust-item">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="var(--formflu-success)">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>No credit card required</span>
            </div>
            <div className="hero-trust-item">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="var(--formflu-success)">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>HIPAA BAA included</span>
            </div>
            <div className="hero-trust-item">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="var(--formflu-success)">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>14-day free trial</span>
            </div>
          </div>
        </div>

        {/* Hero Image - Transformation */}
        <div className="hero-visual">
          <div className="hero-transformation">
            {/* Sick Form */}
            <div className="hero-mascot-card hero-mascot-sick">
              <img 
                src="/images/finalformflumainlogo.png" 
                alt="Sick Form - underperforming, low conversion" 
                className="hero-mascot-img"
              />
              <div className="hero-mascot-stats">
                <span className="hero-stat-label">Before FormFlu</span>
                <div className="hero-stat-bar hero-stat-bar-sick">
                  <div className="hero-stat-fill" style={{ width: '23%' }}></div>
                </div>
                <span className="hero-stat-value text-warning">23% conversion</span>
              </div>
            </div>

            {/* Arrow */}
            <div className="hero-arrow">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <path 
                  d="M24 8v32m0 0l12-12m-12 12L12 28" 
                  stroke="var(--formflu-teal)" 
                  strokeWidth="3" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {/* Healthy Form */}
            <div className="hero-mascot-card hero-mascot-healthy">
              <img 
                src="/images/finalformfluhealthylogo.png" 
                alt="Healthy Form - optimized, high conversion" 
                className="hero-mascot-img"
              />
              <div className="hero-mascot-stats">
                <span className="hero-stat-label">After FormFlu</span>
                <div className="hero-stat-bar hero-stat-bar-healthy">
                  <div className="hero-stat-fill" style={{ width: '67%' }}></div>
                </div>
                <span className="hero-stat-value text-success">67% conversion</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
